"use client"

import { useState } from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useOrganization } from "@/features/settings/hooks/use-organization"
import { authStore } from "@/store/auth"
import { CheckoutProvider, PaymentElement, useCheckout, useStripe } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { LoaderCircle } from "lucide-react"

import { useBillingStore } from "../../store/billing-store"
import { PlanSummary } from "./plan-summary"

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY)

interface CheckoutPaymentFormProps {
  onPaymentSuccess: () => void
  onPaymentError: (error: string) => void
}

export function CheckoutPaymentForm({
  onPaymentSuccess,
  onPaymentError,
}: CheckoutPaymentFormProps) {
  const [error, setError] = useState<string | null>(null)
  const { user } = authStore.getState()
  const { data: organization, isLoading: isOrganizationLoading } = useOrganization(
    user?.organization_id
  )

  const { selectedPlan, createTransparentCheckout } = useBillingStore()

  if (isOrganizationLoading) {
    return <div>Carregando...</div>
  }

  const fetchClientSecret = async () => {
    if (!selectedPlan) return
    console.log("fetching client secret")

    try {
      setError(null)

      // Criar checkout session
      const result = await createTransparentCheckout({
        plan_id: selectedPlan.id,
        customer_info: {
          email: user!.email,
          name: user!.name,
          phone: organization!.whatsapp,
        },
        interval: selectedPlan.interval,
        trial_days: 7,
      })

      // Renderizar o Elements com o clientSecret obtido
      return result?.client_secret
    } catch (err) {
      console.error("Error creating checkout:", err)
      const errorMessage = err instanceof Error ? err.message : "Falha ao criar sessão de pagamento"
      setError(errorMessage)
      onPaymentError(errorMessage)
    }
  }

  const appearance = {
    theme: "stripe" as const,
  }
  const options = {
    fetchClientSecret,
    elementsOptions: {
      appearance,
    },
  }

  if (error) {
    return (
      <div className="mx-auto my-8 max-w-md">
        <Alert variant="destructive">
          <AlertDescription>
            {error ||
              "Não foi possível inicializar o formulário de pagamento. Por favor, tente novamente."}
          </AlertDescription>
        </Alert>
      </div>
    )
  }
  return (
    <div className="mx-auto max-w-5xl overflow-hidden rounded-xl bg-white shadow-sm dark:bg-gray-900">
      <div className="grid md:grid-cols-2">
        {/* Left column - Plan details */}
        <PlanSummary selectedPlan={selectedPlan} />

        {/* Right column - Checkout button */}
        <div className="bg-white p-8 dark:bg-gray-900">
          <div className="mx-auto max-w-md">
            <CheckoutProvider stripe={stripePromise} options={options}>
              <PaymentForm onPaymentSuccess={onPaymentSuccess} onPaymentError={onPaymentError} />
            </CheckoutProvider>
            <div className="mt-4 flex items-center justify-center space-x-2">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                ></path>
              </svg>
              <span className="text-xs text-gray-500">Conexão segura e criptografada</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function PaymentForm({
  onPaymentSuccess,
  onPaymentError,
}: {
  onPaymentSuccess: () => void
  onPaymentError: (error: string) => void
}) {
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const stripe = useStripe()
  const checkout = useCheckout()
  const { submitCheckoutSuccess } = useBillingStore()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!stripe) {
      return
    }

    setIsProcessing(true)
    setError(null)

    try {
      console.log("Payment processing")
      const result = await checkout.confirm({
        returnUrl: window.location.origin + "/billing/success",
        redirect: "if_required",
        savePaymentMethod: true,
      })
      if (result.type === "error") {
        // Show error to your customer (for example, payment details incomplete)
        console.log(result.error.message)
        setError(result.error.message)
      } else {
        await submitCheckoutSuccess({
          session_id: result.session.id,
        })
        console.log("Payment successful")
        onPaymentSuccess()
      }
    } catch (err) {
      console.error("Payment setup error:", err)
      onPaymentError(err instanceof Error ? err.message : "Falha no processamento do cartão")
    } finally {
      setIsProcessing(false)
    }
  }
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* <AddressElement options={{
          mode: "billing",
        }} /> */}
      <PaymentElement
        options={{
          layout: {
            type: "tabs",
            defaultCollapsed: false,
          },
          paymentMethodOrder: ["card", "boleto"],
          fields: {
            billingDetails: {
              address: {
                country: "auto",
              },
              name: "auto",
              email: "auto",
              phone: "auto",
            },
          },
          terms: {
            card: "never",
            bancontact: "never",
          },
        }}
      />

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        type="submit"
        className="h-12 w-full rounded-md bg-indigo-600 text-white transition-colors hover:bg-indigo-700"
        disabled={isProcessing}
      >
        {isProcessing ? (
          <>
            <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            Processando...
          </>
        ) : (
          `Iniciar Teste Grátis`
        )}
      </Button>
    </form>
  )
}
